import { ReactNode, useEffect, useMemo } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos ("Adicionar" ou a barra de seleção). */
  actions?: ReactNode
}

const PacientesListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.empresa ? `empresa.id:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.situacao ? `ativo:${debouncedFilters.situacao.value}` : '',
        debouncedFilters.paciente.trim() ? `pessoa.nome~*${debouncedFilters.paciente.trim()}*` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: 'Paciente',
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.situacao && {
      key: 'situacao',
      label: t('situacao.label'),
      value: filters.situacao.label,
      onRemove: () => setFilter('situacao', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'paciente encontrado' : 'pacientes encontrados'}`
          }
        />
      }
    >
      <TableFilter label='Paciente'>
        <Input
          placeholder='Nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter label={t('situacao.label')}>
        <CustomSelect
          entity='situacao'
          onChange={(situacao?: IOption<boolean>) => setFilter('situacao', situacao ?? undefined)}
          options={OptionsHelper.statusCadastro}
          value={filters.situacao}
        />
      </TableFilter>
    </TableFilters>
  )
}

export { PacientesListSearchComponent }
